import { MonoTypeOperatorFunction } from 'rxjs';
import { tap } from 'rxjs/operators';
import { failedOutcome, MilestoneAttributes, MilestoneContexts, TelemetryService } from './telemetry.service';

export interface OutcomeAttributes<T, K extends keyof MilestoneContexts> {
    success?: (result: T) => Partial<MilestoneAttributes<K>>;
    error?: Partial<MilestoneAttributes<K>>;
}

/**
 * Outside the sign-in pages, a 401 ends the call with neither result nor error (AuthInterceptor returns EMPTY), so
 * it records nothing.
 */
export function trackOutcome<T, K extends keyof MilestoneContexts>(
    telemetry: TelemetryService,
    name: K,
    attributes: MilestoneAttributes<K>,
    outcomeAttributes: OutcomeAttributes<T, K> = {}
): MonoTypeOperatorFunction<T> {
    return tap({
        next: result =>
            record(telemetry, name, () => ({
                ...attributes,
                ...outcomeAttributes.success?.(result),
                outcome: 'success'
            })),
        error: error =>
            record(telemetry, name, () => ({ ...attributes, ...outcomeAttributes.error, ...failedOutcome(error) }))
    });
}

/** Records only a failure, for a step that runs before the call whose outcome the milestone counts. */
export function trackFailure<T, K extends keyof MilestoneContexts>(
    telemetry: TelemetryService,
    name: K,
    attributes: MilestoneAttributes<K>
): MonoTypeOperatorFunction<T> {
    return tap({ error: error => record(telemetry, name, () => ({ ...attributes, ...failedOutcome(error) })) });
}

function record<K extends keyof MilestoneContexts>(telemetry: TelemetryService, name: K, context: () => object): void {
    try {
        telemetry.trackMilestone(name, context() as MilestoneContexts[K]);
    } catch {
        // tap would hand the exception to the caller as an error: a bad attribute loses the event, but never
        // fails a call that succeeded.
    }
}
